import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import styles from "./Planning.module.scss";
import { HeaderMenu } from "../../common/HeaderMenu";
import moment from "moment";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Grid, List } from "react-window";
import clsx from "clsx";
import { Link } from "react-router";
import { apiGetEvents } from "../../api/ApiEvents";
import PlanningDaySummary from "./PlanningDaySummary";
import useMediaQuery, { MOBILE_QUERY } from "../../util/useMediaQuery";
import { buildDays, buildMonths, DATE_FORMAT, monthsAround } from "./planningData";
import { usePlanningMonths, usePlanningSites } from "./usePlanningData";

const DAY_WIDTH = 40;
const ROW_HEIGHT = 40;

// Header rows below the months
const DAYS = "days";
const CUSTOM_EVENTS = "customEvents";
const TOTAL_HOURS = "totalHours";

const DESKTOP_LAYOUT = {
  siteColumnWidth: 240,
  monthsHeight: 30,
  headerRows: [{kind: DAYS, height: 50}, {kind: CUSTOM_EVENTS, height: 40}, {kind: TOTAL_HOURS, height: 40}],
  daysBeforeToday: 7
};

// Phones: narrower site names, a shorter header (events without site become a dot on the day), opens near today
const MOBILE_LAYOUT = {
  siteColumnWidth: 110,
  monthsHeight: 24,
  headerRows: [{kind: DAYS, height: 40}, {kind: TOTAL_HOURS, height: 32}],
  daysBeforeToday: 1
};

/**
 * Header cell of a day, in one of the header rows of the layout.
 */
const HeaderCell = ({columnIndex, rowIndex, style, days, index, headerRows, openDay}) => {
  const day = days[columnIndex];
  const kind = headerRows[rowIndex].kind;

  if (kind === DAYS) {
    // Without the row of events without site, the day shows them and opens them
    const withCustomEvents = !headerRows.some(row => row.kind === CUSTOM_EVENTS);
    const hasEvents = withCustomEvents && index.customEventDays.has(day.key);

    return <div style={style}
                className={clsx(styles.dayCell, styles.headerDay, {
                  [styles.today]: day.isToday,
                  [styles.openable]: withCustomEvents
                })}
                onClick={withCustomEvents ? () => openDay(day.key, null) : undefined}>
      {hasEvents && <span className={styles.customEventsDot}/>}
      {day.dayOfMonth}
      <br/>
      {day.weekday}
    </div>;
  }

  if (kind === CUSTOM_EVENTS) {
    const hasEvents = index.customEventDays.has(day.key);

    return <div style={style}
                className={clsx(styles.dayCell, styles.openable, styles.customEvents, {
                  [styles.today]: day.isToday,
                  [styles.we]: day.isWeekend,
                  [styles.active]: hasEvents,
                  [styles.events]: hasEvents
                })}
                onClick={() => openDay(day.key, null)}>
      {hasEvents && <div className={styles.activeSiteWrapper}/>}
    </div>;
  }

  const hours = index.totalHours.get(day.key);

  return <div style={style}
              className={clsx(styles.dayCell, styles.totalHours, {
                [styles.today]: day.isToday,
                [styles.we]: day.isWeekend
              })}>
    <div>{hours > 0 ? hours + "h" : <span>&nbsp;</span>}</div>
  </div>;
};

/**
 * A site's day: active period, events and prestation hours.
 */
const SiteDayCell = ({columnIndex, rowIndex, style, days, sites, index, hoveredRow, setHoveredRow, openDay}) => {
  const day = days[columnIndex];
  const site = sites[rowIndex];

  const siteDay = index.siteDays.get(site.id + "|" + day.key);
  const hasEvents = siteDay?.hasEvents;
  const isActive = site.dateStart <= day.key && day.key <= site.activeEnd;

  return <div style={style}
              className={clsx(styles.dayCell, styles.openable, {
                [styles.hovered]: rowIndex === hoveredRow,
                [styles.today]: day.isToday,
                [styles.active]: isActive || hasEvents,
                [styles.we]: day.isWeekend,
                [styles.events]: hasEvents
              })}
              onMouseEnter={() => setHoveredRow(rowIndex)}
              onClick={() => openDay(day.key, site)}>
    {(isActive || hasEvents) && <div className={styles.activeSiteWrapper}/>}
    <div className={styles.prestation}>
      <span>{siteDay?.hours > 0 ? siteDay.hours + "h" : null}</span>
    </div>
  </div>;
};

const SiteNameRow = ({index, style, sites, setHoveredRow}) => {
  const site = sites[index];
  const siteColor = site.color.split("|");

  return <div style={style} className={styles.siteName} onMouseEnter={() => setHoveredRow(index)}>
    <Link to={"/sites/" + site.id}
          className={styles.siteNameWrapper}
          style={{backgroundColor: siteColor[0], color: siteColor[1]}}>
      {site.name}
    </Link>
  </div>;
};

/**
 * Only the visible cells are rendered, and the events and prestations are loaded month by month
 * for the visible days. Everything is in one scrolling element: the months and the header rows stick to the top,
 * the site names to the left, so the browser moves them together with the day cells.
 */
const Planning = () => {
  const scrolledToToday = useRef(false);

  const queryClient = useQueryClient();

  // The element that scrolls; the grids render the cells visible in it
  const [scroller, setScroller] = useState(null);
  const [daySummary, setDaySummary] = useState({date: moment(), site: null, events: [], shown: false});
  const [visibleMonths, setVisibleMonths] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);

  const layout = useMediaQuery(MOBILE_QUERY) ? MOBILE_LAYOUT : DESKTOP_LAYOUT;

  const today = useMemo(() => moment().startOf("day"), []);

  const {sitesQuery, sites} = usePlanningSites();

  const days = useMemo(() => {
    let minStart = today.clone().subtract(1, "month").format(DATE_FORMAT);
    let maxEnd = today.clone().add(1, "month").format(DATE_FORMAT);

    sites.forEach(site => {
      minStart = site.dateStart < minStart ? site.dateStart : minStart;
      maxEnd = site.activeEnd > maxEnd ? site.activeEnd : maxEnd;
    });

    const minDate = moment(minStart, DATE_FORMAT).subtract(15, "days").startOf("month");
    const maxDate = moment(maxEnd, DATE_FORMAT).add(1, "month").endOf("month");

    return buildDays(minDate, maxDate.diff(minDate, "days") + 1, today);
  }, [sites, today]);

  const months = useMemo(() => buildMonths(days), [days]);
  const todayIndex = useMemo(() => days.findIndex(day => day.isToday), [days]);

  const {index} = usePlanningMonths(visibleMonths, sites.length > 0);

  const daysWidth = days.length * DAY_WIDTH;
  const headerHeight = layout.monthsHeight + layout.headerRows.reduce((acc, row) => acc + row.height, 0);

  const scrollToToday = useCallback(() => {
    scroller?.scrollTo({left: Math.max(todayIndex - layout.daysBeforeToday, 0) * DAY_WIDTH});
  }, [scroller, todayIndex, layout]);

  const showMonths = useCallback((firstIndex, lastIndex) => {
    const next = monthsAround(days, firstIndex, lastIndex);

    setVisibleMonths(previous => previous.join() === next.join() ? previous : next);
  }, [days]);

  const onCellsRendered = useCallback(visibleCells => {
    // Before the first scroll to today, the grid shows the first days: their months are not needed
    if (scrolledToToday.current) {
      showMonths(visibleCells.columnStartIndex, visibleCells.columnStopIndex);
    }
  }, [showMonths]);

  // Opens on today once the sites are loaded (the content then has its full width)
  useLayoutEffect(() => {
    if (scrolledToToday.current || !sitesQuery.isSuccess || !scroller) {
      return;
    }

    scrolledToToday.current = true;
    scrollToToday();
    showMonths(todayIndex - layout.daysBeforeToday, todayIndex + layout.daysBeforeToday);
  }, [sitesQuery.isSuccess, scroller, scrollToToday, showMonths, todayIndex, layout]);

  const showDaySummary = useCallback((date, site) => {
    return queryClient
      .fetchQuery(apiGetEvents({date: date.format(DATE_FORMAT)}))
      .then(data => setDaySummary({date, site, events: data, shown: true}));
  }, [queryClient]);

  const openDay = useCallback(
    (dayKey, site) => showDaySummary(moment(dayKey, DATE_FORMAT), site),
    [showDaySummary]
  );

  return <Content>
    <ContentFixed>
      <HeaderMenu title={"Planning"}/>
    </ContentFixed>

    <ContentScroll paddingTop className={styles.planningScroll}>
      <div className={styles.navActions}>
        <div className={clsx(styles.navBtn, styles.today)} onClick={scrollToToday}>
          Aujourd'hui
        </div>
      </div>

      <div className={styles.planning} ref={setScroller} onMouseLeave={() => setHoveredRow(null)}>
        <div className={styles.planningContent}
             style={{
               gridTemplateColumns: layout.siteColumnWidth + "px " + daysWidth + "px",
               gridTemplateRows: headerHeight + "px " + (sites.length * ROW_HEIGHT) + "px"
             }}>
          <div className={styles.corner}>
            <div className={styles.cornerMonths} style={{height: layout.monthsHeight}}/>
            {layout.headerRows.map(row => <div key={row.kind} className={styles.cornerRow} style={{height: row.height}}>
              {row.kind === TOTAL_HOURS ? "Total heures" : null}
            </div>)}
          </div>

          <div className={styles.header}>
            <div className={styles.months} style={{height: layout.monthsHeight}}>
              {months.map(month => <div key={month.month}
                                        className={styles.month}
                                        style={{left: month.startIndex * DAY_WIDTH, width: month.nbDays * DAY_WIDTH}}>
                <span className={styles.monthLabel} style={{left: layout.siteColumnWidth}}>{month.label}</span>
              </div>)}
            </div>

            <Grid cellComponent={HeaderCell}
                  cellProps={{days, index, headerRows: layout.headerRows, openDay}}
                  columnCount={days.length}
                  columnWidth={DAY_WIDTH}
                  rowCount={layout.headerRows.length}
                  rowHeight={rowIndex => layout.headerRows[rowIndex].height}
                  scrollElement={scroller}
                  style={{width: "max-content"}}/>
          </div>

          <div className={styles.siteNames}>
            <List rowComponent={SiteNameRow}
                  rowProps={{sites, setHoveredRow}}
                  rowCount={sites.length}
                  rowHeight={ROW_HEIGHT}
                  scrollElement={scroller}/>
          </div>

          <Grid cellComponent={SiteDayCell}
                cellProps={{days, sites, index, hoveredRow, setHoveredRow, openDay}}
                columnCount={days.length}
                columnWidth={DAY_WIDTH}
                rowCount={sites.length}
                rowHeight={ROW_HEIGHT}
                overscanCount={3}
                onCellsRendered={onCellsRendered}
                scrollElement={scroller}
                style={{width: "max-content"}}/>
        </div>
      </div>

      <PlanningDaySummary
        daySummary={daySummary}
        setDaySummary={setDaySummary}
        onEventSubmitted={() => {
          return Promise.all([
            queryClient.invalidateQueries({queryKey: ["planning_events"]}),
            showDaySummary(daySummary.date, daySummary.site)
          ]);
        }}/>
    </ContentScroll>
  </Content>;
};

export default Planning;
